// Audio node management for the audio engine

export interface AudioNodeManagerOptions {
  audioContext: AudioContext;
  createDevice: (audioContext: AudioContext) => Promise<any>;
}

export class AudioNodeManager {
  private audioContext: AudioContext;
  private createDevice: (audioContext: AudioContext) => Promise<any>;
  private gainNode: GainNode;
  private analyserNode: AnalyserNode;
  private device: any = null;
  private isSetup = false;

  constructor(options: AudioNodeManagerOptions) {
    this.audioContext = options.audioContext;
    this.createDevice = options.createDevice;

    // Create gain node
    this.gainNode = new GainNode(this.audioContext, { gain: 1.0 });
    console.log('🎚️ Created gain node with gain:', this.gainNode.gain.value);

    // Create analyser node for level metering
    this.analyserNode = new AnalyserNode(this.audioContext, {
      fftSize: 256,
      minDecibels: -90,
      maxDecibels: -10,
      smoothingTimeConstant: 0.8
    });
    console.log('📊 Created analyser node for level metering');
  }

  /**
   * Setup audio device and connections
   */
  async setup(): Promise<void> {
    if (this.isSetup) {
      return;
    }

    // Create device
    this.device = await this.createDevice(this.audioContext);

    // Connect gain to both analyser and device
    this.gainNode.connect(this.analyserNode);
    this.gainNode.connect(this.device.node);

    // Connect device to audio destination
    this.device.node.connect(this.audioContext.destination);

    console.log('🔊 Connected audio graph: worklet → gain → analyser & device → destination');

    this.isSetup = true;
  }

  /**
   * Get the gain node
   */
  getGainNode(): GainNode {
    return this.gainNode;
  }

  /**
   * Get the input node (same as gain node for audio graph routing)
   */
  getInputNode(): GainNode {
    return this.gainNode;
  }

  /**
   * Get the analyser node
   */
  getAnalyserNode(): AnalyserNode {
    return this.analyserNode;
  }

  /**
   * Get the device
   */
  getDevice(): any {
    return this.device;
  }

  /**
   * Get current gain value
   */
  getGain(): number {
    return this.gainNode.gain.value;
  }

  /**
   * Set gain value
   * @param value Gain value (0-1)
   */
  setGain(value: number): void {
    this.gainNode.gain.value = value;
  }

  /**
   * Connect an audio node to the gain input
   * @param node Audio node to connect
   */
  connectToInput(node: AudioNode): void {
    node.connect(this.gainNode);
  }

  /**
   * Connect gain output to a device node
   * @param deviceNode The device node to connect to
   */
  connectToDevice(deviceNode: AudioNode): void {
    if (this.device && this.device.node) {
      this.device.node.disconnect();
    }
    this.device = { node: deviceNode };
    this.gainNode.connect(this.analyserNode);
    this.gainNode.connect(deviceNode);
    deviceNode.connect(this.audioContext.destination);
  }

  /**
   * Disconnect all nodes and cleanup
   */
  teardown(): void {
    try {
      if (this.device) {
        this.device.node.disconnect();
        this.device = null;
      }

      this.gainNode.disconnect();
      this.analyserNode.disconnect();

      this.isSetup = false;
    } catch (error) {
      console.error('Error during audio node teardown:', error);
    }
  }

  /**
   * Check if setup is complete
   */
  getIsSetup(): boolean {
    return this.isSetup;
  }

  /**
   * Get audio context
   */
  getAudioContext(): AudioContext {
    return this.audioContext;
  }

  /**
   * Get level meter data from analyser
   * @returns Float32Array of frequency data
   */
  getFrequencyData(): Float32Array {
    const dataArray = new Float32Array(this.analyserNode.frequencyBinCount);
    this.analyserNode.getFloatFrequencyData(dataArray);
    return dataArray;
  }

  /**
   * Get time domain data from analyser
   * @returns Float32Array of time domain data
   */
  getTimeDomainData(): Float32Array {
    const dataArray = new Float32Array(this.analyserNode.fftSize);
    this.analyserNode.getFloatTimeDomainData(dataArray);
    return dataArray;
  }

  /**
   * Calculate RMS level from time domain data
   * @returns RMS level (0-1)
   */
  getRMSLevel(): number {
    const data = this.getTimeDomainData();
    let sum = 0;

    for (let i = 0; i < data.length; i++) {
      sum += data[i] * data[i];
    }

    const rms = Math.sqrt(sum / data.length);
    return Math.min(1, rms);
  }

  /**
   * Get peak level from time domain data
   * @returns Peak level (0-1)
   */
  getPeakLevel(): number {
    const data = this.getTimeDomainData();
    let peak = 0;

    for (let i = 0; i < data.length; i++) {
      peak = Math.max(peak, Math.abs(data[i]));
    }

    return Math.min(1, peak);
  }
}
